import 'rxjs/add/operator/filter';
import type { RelayObservable } from 'relay-runtime/lib/network/RelayObservable';
import { Observable } from 'rxjs/Observable';
import type { Subscriber } from 'rxjs/Subscriber';
import type { ActionsObservable, Dependencies, MiddlewareAPI, ReduxEpic } from '../../state/types';

type PredicateFunction = () => boolean;
type Predicate = PredicateFunction | boolean;

function executePredicate(predicate: Predicate): boolean {
	if (predicate !== null && typeof predicate === 'function') {
		return predicate();
	}
	if (predicate !== null && typeof predicate === 'boolean') {
		return predicate;
	}
	return false;
}

/** Accepts a list of predicates and returns either an epic that matches the conditions or the other one.
 *
 * @param   predicates   A list of predicate which could be a function that returns a {boolean} or primitive {boolean} type.
 * @returns {(action$: ActionsObservable, store: MiddlewareAPI) => ObservableType<Action>} a single redux-observable epic

 * @example
 * const isFooFeatureFlagEnabled(): boolean => ...;
 * const isBarFeatureFlagEnabled(): boolean => ...;
 * const isBazSetup: boolean = true;
 *
 * withSwitch(isFooFeatureFlagEnabled)(fooEpic, barEpic)
 * withSwitch(isFooFeatureFlagEnabled, isBarFeatureFlagEnabled)(fooEpic, barEpic)
 * withSwitch(isFooFeatureFlagEnabled, isBazSetup)(fooEpic, barEpic)
 * withSwitch(isFooFeatureFlagEnabled, isBarFeatureFlagEnabled, isBazSetup)(fooEpic, barEpic)
 * */
export const withSwitch =
	(...predicates: Predicate[]) =>
	(matchedPredicateEpic: ReduxEpic, unmatchedPredicateEpic: ReduxEpic): ReduxEpic =>
	(action$: ActionsObservable, store: MiddlewareAPI, dependencies?: Dependencies) =>
		predicates.every(executePredicate)
			? matchedPredicateEpic(action$, store, dependencies)
			: unmatchedPredicateEpic(action$, store, dependencies);

/** Accepts a list of predicates and returns a function which can be used
 * to wrap an epic. If any of the predicates are not met, all observable 
 * actions will be filtered (excluded) from the wrapped epic.
 *
 * @param   predicates   A list of predicate which could be a function that returns a {boolean} or primitive {boolean} type.
 * @returns {
 *  (epic: ReduxEpic) => (action$: ActionsObservable, store: MiddlewareAPI) => ObservableType<Action> 
 * } a higher-order function which returns a single redux-observable epic

 * @example
 * const isFooFeatureFlagEnabled(): boolean => ...;
 * const isBarFeatureFlagEnabled(): boolean => ...;
 * const isBazSetup: boolean = true;
 * const fooFeatureEpicKillswitch = withKillSwitch(isFooFeatureFlagEnabled)
 * 
 * fooFeatureEpicKillswitch(fooEpic)
 * fooFeatureEpicKillswitch(barEpic)
 * withKillSwitch(isFooFeatureFlagEnabled, isBazSetup)(fooEpic)
 * withKillSwitch(isFooFeatureFlagEnabled, isBarFeatureFlagEnabled, isBazSetup)(barEpic)
 * */
export const withKillSwitch =
	(...predicates: Predicate[]) =>
	(epics: ReduxEpic[]): ReduxEpic[] =>
		epics.map(
			(epic) => (action$: ActionsObservable, store: MiddlewareAPI, dependencies?: Dependencies) =>
				epic(
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					action$.filter(() => predicates.every(executePredicate)) as ActionsObservable,
					store,
					dependencies,
				),
		);

export const fromRelayObservable = <T,>(create: () => RelayObservable<T>): Observable<T> =>
	Observable.create((sink: Subscriber<T>) => {
		const subscription = create().subscribe(sink);

		return () => subscription.unsubscribe();
	});
